import "./NavBarNew.scss";

import {
  ChatIcon,
  Flex,
  Input,
  Menu,
  MenuItemProps,
  MenuShorthandKinds,
  OptionsIcon,
  SearchIcon,
  SettingsIcon,
  ShorthandCollection,
} from "@fluentui/react-northstar";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { useHubspotChat } from "../../actions/useHubspotChat";
import { usePaymentRedirect } from "../../actions/usePaymentRedirect";
import { useUserInfo } from "../../actions/useUserInfo";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { isMobileClient } from "../../utils/clientHelper";
import { clientEventTracker } from "../../utils/clientTelemetry";
import { startCheckoutTask } from "../../utils/startTaskWrapper";
import { getPageContext } from "../../utils/teamsHelpers";
import useWindowDimensions from "../../utils/useWindowDimensions";
import { PaymentRedirectPlaceholder } from "../Checkout/PaymentRedirectPlaceholder";
import { PollyDialog } from "../PollyDialog";
import { PollyIcon } from "../PollyIcon";
import { QuickWalkThroughDialog } from "../QuickWalkThroughDialog";
import { SubscriptionStateDisplay } from "./SubscriptionStateDisplay";
import { UpgradeMessageDisplay } from "./UpgradeMessageDisplay";

type Props = {
  onSearch?: (value: string) => Promise<void>;
};

export const NavBarNew: React.FC<Props> = ({ onSearch }) => {
  const [isSearching, setIsSearching] = useState<boolean>();
  const { teamsContext } = useTeamsContext();
  const isMobile = isMobileClient(teamsContext);

  return (
    <Flex className="navbarnew" vAlign="center" gap={`${isMobile ? "gap.small" : "gap.medium"}`}>
      <Flex gap="gap.large" vAlign="center">
        <SubscriptionStateDisplay isMobile={isMobile} />
      </Flex>

      <Flex gap="gap.large" vAlign="center">
        <UpgradeMessageDisplay isMobile={isMobile} />
      </Flex>

      <Flex.Item grow>
        <div />
      </Flex.Item>

      <Flex.Item push>
        <>
          {onSearch && (
            <Input
              className={isMobile ? "search-field" : ""}
              icon={<SearchIcon />}
              disabled={isSearching}
              iconPosition="end"
              placeholder="Search"
              clearable
              maxLength={100}
              onChange={async (_, input) => {
                if (!input?.value) {
                  setIsSearching(true);
                  await onSearch("");
                  setIsSearching(false);
                }
              }}
              onKeyPress={async event => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  setIsSearching(true);
                  try {
                    await onSearch(event.currentTarget.value);
                  } finally {
                    setIsSearching(false);
                  }
                }
              }}
            />
          )}

          <UserMenu />
        </>
      </Flex.Item>
    </Flex>
  );
};

const UserMenu: React.FC = () => {
  const history = useHistory();
  const { teamsContext } = useTeamsContext();
  const { user, loadUser } = useUserInfo();
  const [isQuickWalkthroughDialogVisible, setIsQuickWalkthroughDialogVisible] = useState<boolean>();
  const [isCheckoutOpen, setIsCheckoutOpen] = useState<boolean>();
  const { loadHubspotChat } = useHubspotChat();
  const { openPaymentPage } = usePaymentRedirect();
  const { width } = useWindowDimensions();

  const subMenuItems = useMemo(() => {
    const activeSub =
      user.subscriptions.find(s => s.state === "active") ??
      user.adminSubscriptions.find(s => s.state === "active");

    const items: ShorthandCollection<MenuItemProps, MenuShorthandKinds> = [
      {
        key: "quick-start-walkthrough",
        content: "Quick Start Walkthrough",
        icon: <PollyIcon icon="youtube-play-icon" inheritColor />,
        onClick: () => {
          if (getPageContext() === "/agile") {
            window.open("https://www.youtube.com/watch?v=ztlLbqVmXeg", "_blank");
          } else {
            setIsQuickWalkthroughDialogVisible(true);
          }
        },
      },
      {
        key: "contact-support",
        content: "Contact Support",
        icon: <ChatIcon />,
        onClick: async () => {
          if (user.subscriptions.length > 0) {
            await loadHubspotChat(teamsContext, true);
          } else {
            const context = getPageContext();
            if (context === "/agile") {
              window.open("https://www.polly.ai/help/microsoft-teams-agile", "_blank");
            } else {
              window.open("https://www.polly.ai/help/microsoft-teams", "_blank");
            }
          }
        },
      },
    ];

    if (activeSub?.type === "full") {
      items.unshift({
        key: "account-settings",
        content: "Account Settings",
        icon: <OptionsIcon />,
        onClick: () => history.push(`${getPageContext()}/content/account`),
      });
    }

    if ((!activeSub || activeSub.type === "trial") && !isMobileClient(teamsContext)) {
      items.push({
        key: "divider-subscribe",
        kind: "divider",
      });

      items.push({
        key: "subscribe",
        content: "Upgrade plan",
        icon: <PollyIcon icon="pay-icon" />,
        primary: true,
        onClick: () => {
          clientEventTracker.track("UpgradeButton clicked", user, {
            trackingSource: "usermenu",
          });
          if (getPageContext() === "/agile") {
            openPaymentPage("usermenu");
            setIsCheckoutOpen(true);
          } else {
            startCheckoutTask(false, "usermenu", width, () => {
              loadUser(true);
            });
          }
        },
      });
    }

    return items;
  }, [user, teamsContext, loadHubspotChat, history, openPaymentPage, width, loadUser]);

  return (
    <>
      <Menu
        iconOnly
        items={[
          {
            key: "user-menu",
            icon: <SettingsIcon size="large" />,
            iconOnly: true,
            menu: {
              items: subMenuItems,
            },
          },
        ]}
      />

      {isQuickWalkthroughDialogVisible && getPageContext() === "/msteams" && (
        <QuickWalkThroughDialog open onClose={() => setIsQuickWalkthroughDialogVisible(false)} />
      )}

      {isCheckoutOpen && (
        <PollyDialog
          width="90vw"
          maxWidth="700px"
          onCancel={() => {
            loadUser(true);
            setIsCheckoutOpen(false);
          }}
          closeOnOutsideClick={false}
          open={isCheckoutOpen}
        >
          <PaymentRedirectPlaceholder
            actionText="Close"
            trackingSource="usermenu"
            onActionClick={() => {
              loadUser();
              setIsCheckoutOpen(false);
            }}
          />
        </PollyDialog>
      )}
    </>
  );
};
